import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText, Download, Loader2, ChevronRight, ArrowLeft, Info, MoreHorizontal, Undo2,
} from 'lucide-react';
import { toast } from 'sonner';
import EarningsSummary from '../components/EarningsSummary';
import { apiService } from '../services/api.service';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DateTimeInput } from '../components/ui/date-time-input';
import { refuseIfDateInputIncomplete } from '../lib/dateInputs';

/**
 * Les relevés de ventes de l'organisateur — documents émis EN SON NOM.
 *
 * Deux états, et la distinction est le cœur de l'écran :
 *  - mois en cours : lisible, rien de plus. Pas de numéro, pas de bouton.
 *  - mois clos     : numéroté, figé, téléchargeable.
 *
 * Un document qui circule est un document définitif : le bouton n'apparaît
 * jamais sur un brouillon.
 */

interface StatementRow {
  year: number;
  month: number;
  sales_count: number;
  total_gross_cents: number;
  commission_cents: number;
  status: 'draft' | 'issued';
  statement_number: string | null;
  issued_at: string | null;
  period_closed: boolean;
  downloadable: boolean;
}

interface StatementLine {
  payment_id: string | null;
  external_invoice_number: string | null;
  external_invoice_date: string | null;
  sold_at: string;
  event_name: string | null;
  category_name: string | null;
  team_name: string | null;
  buyer_name: string | null;
  amount_gross_cents: number;
  vat_rate: number;
  amount_vat_cents: number;
  amount_net_cents: number;
}

interface CreditNoteLine {
  origin_statement_number: string | null;
  origin_sold_at: string | null;
  event_name: string | null;
  team_name: string | null;
  buyer_name: string | null;
  amount_gross_cents: number;
  amount_vat_cents: number;
  amount_net_cents: number;
}

interface CreditNote {
  year: number;
  month: number;
  credit_note_number: string;
  issued_at: string;
  vat_rate: number;
  legal_mention: string | null;
  lines: CreditNoteLine[];
  totals: { gross_cents: number; vat_cents: number; net_cents: number };
}

interface StatementDetail {
  year: number;
  month: number;
  status: 'draft' | 'issued';
  statement_number: string | null;
  issued_at: string | null;
  vat_status: string | null;
  vat_rate: number;
  legal_mention: string | null;
  lines: StatementLine[];
  /** Ventes que l'organisateur a facturées lui-même : hors total, dans le versement. */
  invoiced_lines: StatementLine[];
  totals: {
    invoiced_elsewhere_cents: number;
    gross_cents: number;
    vat_cents: number;
    net_cents: number;
    commission_cents: number;
    refunds_cents: number;
    transferred_cents: number;
  };
  refunds_known?: boolean;
  downloadable: boolean;
}

interface Invoice {
  id: number;
  kind: 'commission' | 'tokens';
  number: string;
  date: string | null;
  amount_total_cents: number;
  period_year: number | null;
  period_month: number | null;
  statement_number: string | null;
}

const euros = (cents: number) =>
  new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(cents / 100);

const Statements: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<StatementRow[] | null>(null);
  const [detail, setDetail] = useState<StatementDetail | null>(null);
  // La note de crédit n'existe QUE si un remboursement a touché une vente déjà
  // figée : le plus souvent il n'y en a aucune, et on n'affiche alors rien.
  const [creditNote, setCreditNote] = useState<CreditNote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Les factures de Pax (commission, jetons), lues dans Odoo. `null` tant
  // qu'on ne sait pas ; une panne d'Odoo s'affiche à part, sans masquer les
  // relevés.
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);
  // La vente dont l'organisateur déclare la facture. `null` = fenêtre fermée.
  const [invoiceTarget, setInvoiceTarget] = useState<StatementLine | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [savingInvoice, setSavingInvoice] = useState(false);

  const monthLabel = (year: number, month: number) =>
    new Date(year, month - 1, 1).toLocaleDateString(i18n.language, {
      month: 'long',
      year: 'numeric',
    });

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.get('/organizations/me/statements');
      setRows(res.data.statements as StatementRow[]);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('statements.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    apiService
      .get('/organizations/me/invoices')
      .then((res) => setInvoices(res.data.invoices as Invoice[]))
      .catch((err: any) =>
        setInvoicesError(err?.response?.data?.detail || t('statements.invoices.loadError'))
      );
  }, [t]);

  // Une erreur de téléchargement arrive en blob : on en relit le message du
  // serveur, qui nomme la cause mieux qu'un texte générique.
  const messageErreurTelechargement = async (err: any): Promise<string> => {
    const data = err?.response?.data;
    if (data instanceof Blob) {
      try {
        return JSON.parse(await data.text()).detail || t('statements.downloadError');
      } catch {
        /* message générique */
      }
    }
    return t('statements.downloadError');
  };

  const downloadInvoice = async (invoice: Invoice) => {
    try {
      await apiService.downloadFile(
        `/organizations/me/invoices/${invoice.id}/pdf`,
        `${invoice.number.replace(/\//g, '_')}.pdf`
      );
    } catch (err: any) {
      setInvoicesError(await messageErreurTelechargement(err));
    }
  };

  const openDetail = async (row: StatementRow) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.get(`/organizations/me/statements/${row.year}/${row.month}`);
      setDetail(res.data as StatementDetail);
      // Chargée à part : son absence est le cas normal, elle ne doit pas faire
      // échouer l'affichage du relevé.
      try {
        const avoir = await apiService.get(
          `/organizations/me/statements/${row.year}/${row.month}/credit-note`
        );
        setCreditNote((avoir.data?.credit_note as CreditNote) || null);
      } catch {
        setCreditNote(null);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('statements.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // Par l'API et non par un lien direct : un lien n'emporte pas l'en-tête de
  // l'organisation qu'un admin consulte (voir apiService.downloadFile).
  const download = async (year: number, month: number, creditNote = false) => {
    const chemin = `/organizations/me/statements/${year}/${month}/${creditNote ? 'credit-note/pdf' : 'pdf'}`;
    try {
      await apiService.downloadFile(chemin, `${creditNote ? 'note-credit' : 'releve'}-${year}-${String(month).padStart(2, '0')}.pdf`);
    } catch (err: any) {
      setError(await messageErreurTelechargement(err));
    }
  };

  const reloadDetail = async (year: number, month: number) => {
    const res = await apiService.get(`/organizations/me/statements/${year}/${month}`);
    setDetail(res.data as StatementDetail);
  };

  const downloadProof = async (line: StatementLine) => {
    if (!line.payment_id) return;
    try {
      await apiService.downloadFile(
        `/organizations/me/statements/sales/${line.payment_id}/payment-proof`,
        'preuve-de-paiement.pdf'
      );
    } catch (err: any) {
      toast.error(await messageErreurTelechargement(err));
    }
  };

  const openInvoiceDialog = (line: StatementLine) => {
    setInvoiceTarget(line);
    setInvoiceNumber('');
    setInvoiceDate(new Date().toISOString().slice(0, 10));
    setInvoiceError(null);
  };

  const saveInvoice = async () => {
    if (!detail || !invoiceTarget?.payment_id) return;
    if (refuseIfDateInputIncomplete(t)) return;
    if (!invoiceNumber.trim()) {
      setInvoiceError(t('statements.externalInvoice.numberRequired'));
      return;
    }
    if (!invoiceDate) {
      setInvoiceError(t('statements.externalInvoice.dateRequired'));
      return;
    }
    setSavingInvoice(true);
    try {
      await apiService.put(
        `/organizations/me/statements/sales/${invoiceTarget.payment_id}/external-invoice`,
        { invoice_number: invoiceNumber.trim(), invoice_date: invoiceDate }
      );
      await reloadDetail(detail.year, detail.month);
      setInvoiceTarget(null);
      toast.success(t('statements.externalInvoice.saved'));
    } catch (err: any) {
      setInvoiceError(err?.response?.data?.detail || t('statements.externalInvoice.saveError'));
    } finally {
      setSavingInvoice(false);
    }
  };

  const removeInvoice = async (line: StatementLine) => {
    if (!detail || !line.payment_id) return;
    try {
      await apiService.delete(
        `/organizations/me/statements/sales/${line.payment_id}/external-invoice`
      );
      await reloadDetail(detail.year, detail.month);
      toast.success(t('statements.externalInvoice.removed'));
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('statements.externalInvoice.saveError'));
    }
  };

  if (loading && !rows && !detail) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ── Détail d'un mois ──────────────────────────────────────────────────────
  if (detail) {
    const withVat = detail.vat_rate > 0;
    const issued = detail.status === 'issued';
    const invoicedLines = detail.invoiced_lines || [];

    // Le menu d'une vente. Sur un mois émis, la déclaration est verrouillée et
    // le menu DIT pourquoi : la vente est déjà dans un document qui a circulé.
    const saleMenu = (line: StatementLine) =>
      line.payment_id && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" aria-label={t('statements.externalInvoice.actions')}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuItem disabled={issued} onSelect={() => openInvoiceDialog(line)}>
              {t('statements.externalInvoice.declare')}
            </DropdownMenuItem>
            {issued && (
              <p className="px-2 pb-2 text-xs text-muted-foreground">
                {t('statements.externalInvoice.lockedIssued')}
              </p>
            )}
            <DropdownMenuItem onSelect={() => downloadProof(line)}>
              {t('statements.externalInvoice.downloadProof')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );

    return (
      <div className="p-4 md:p-6 space-y-4 md:space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDetail(null);
                setCreditNote(null);
              }}
              className="mt-0.5"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold capitalize md:text-2xl">
                {monthLabel(detail.year, detail.month)}
              </h1>
              <p className="text-xs text-muted-foreground md:text-sm">
                {detail.status === 'issued'
                  ? t('statements.issuedOn', {
                      number: detail.statement_number,
                      date: new Date(detail.issued_at as string).toLocaleDateString(i18n.language),
                    })
                  : t('statements.draftNotice')}
              </p>
            </div>
          </div>
          {detail.downloadable && (
            <Button
              size="sm"
              className="shrink-0 self-start sm:self-auto"
              onClick={() => download(detail.year, detail.month)}
            >
              <Download className="mr-2 h-4 w-4" />
              {t('statements.download')}
            </Button>
          )}
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {detail.status === 'draft' && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>{t('statements.draftExplanation')}</AlertDescription>
          </Alert>
        )}

        {/* Bloc 1 — les ventes. Chaque ligne porte le PRIX PAYÉ : la commission
            n'y entre jamais, elle vit dans le bloc du bas. */}
        <div className="rounded-lg border bg-card">
          <div className="border-b px-4 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide">
              {t('statements.salesBlock')}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-medium">{t('statements.columns.date')}</th>
                  <th className="px-4 py-2 font-medium">{t('statements.columns.event')}</th>
                  <th className="px-4 py-2 font-medium">{t('statements.columns.category')}</th>
                  <th className="px-4 py-2 font-medium">{t('statements.columns.team')}</th>
                  {withVat && (
                    <>
                      <th className="px-4 py-2 text-right font-medium">{t('statements.columns.net')}</th>
                      <th className="px-4 py-2 text-right font-medium">{t('statements.columns.vat')}</th>
                    </>
                  )}
                  <th className="px-4 py-2 text-right font-medium">
                    {withVat ? t('statements.columns.gross') : t('statements.columns.amount')}
                  </th>
                  <th className="w-10 px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {detail.lines.map((line, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="whitespace-nowrap px-4 py-2 text-muted-foreground tabular-nums">
                      {new Date(line.sold_at).toLocaleDateString(i18n.language, {
                        day: '2-digit',
                        month: '2-digit',
                      })}
                    </td>
                    <td className="px-4 py-2">{line.event_name || '—'}</td>
                    <td className="px-4 py-2 text-muted-foreground">{line.category_name || '—'}</td>
                    <td className="px-4 py-2">{line.team_name || line.buyer_name || '—'}</td>
                    {withVat && (
                      <>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {euros(line.amount_net_cents)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                          {euros(line.amount_vat_cents)}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-2 text-right font-medium tabular-nums">
                      {euros(line.amount_gross_cents)}
                    </td>
                    <td className="px-2 py-1 text-right">{saleMenu(line)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 font-semibold">
                  <td className="px-4 py-2" colSpan={4}>
                    {t('statements.totalSales', { count: detail.lines.length })}
                  </td>
                  {withVat && (
                    <>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {euros(detail.totals.net_cents)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {euros(detail.totals.vat_cents)}
                      </td>
                    </>
                  )}
                  <td className="px-4 py-2 text-right tabular-nums">
                    {euros(detail.totals.gross_cents)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
          {detail.legal_mention && (
            <p className="border-t px-4 py-3 text-xs text-muted-foreground">
              {detail.legal_mention}
            </p>
          )}
          {/* Le malentendu à désamorcer : prendre le montant VIRÉ pour son
              chiffre d'affaires. C'est le brut ci-dessus qu'il déclare. */}
          <p className="border-t px-4 py-3 text-xs text-muted-foreground">
            {t('statements.revenueNotice')}
          </p>
        </div>

        {/* Ventes facturées par l'organisateur : hors total (déjà dans son
            journal par sa facture), mais dans le versement. */}
        {invoicedLines.length > 0 && (
          <div className="rounded-lg border bg-card">
            <div className="border-b px-4 py-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide">
                {t('statements.externalInvoice.block')}
              </h2>
              <p className="text-xs text-muted-foreground">{t('statements.externalInvoice.blockHint')}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  {invoicedLines.map((line) => (
                    <tr key={line.payment_id || line.sold_at} className="border-b last:border-0">
                      <td className="whitespace-nowrap px-4 py-2 text-muted-foreground tabular-nums">
                        {new Date(line.sold_at).toLocaleDateString(i18n.language, {
                          day: '2-digit',
                          month: '2-digit',
                        })}
                      </td>
                      <td className="px-4 py-2">{line.team_name || line.buyer_name || '—'}</td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {t('statements.externalInvoice.invoiceRef', {
                          number: line.external_invoice_number,
                          date: line.external_invoice_date
                            ? new Date(line.external_invoice_date).toLocaleDateString(i18n.language)
                            : '',
                        })}
                      </td>
                      <td className="px-4 py-2 text-right font-medium tabular-nums">
                        {euros(line.amount_gross_cents)}
                      </td>
                      <td className="w-20 px-2 py-1 text-right whitespace-nowrap">
                        {!issued && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => removeInvoice(line)}
                            aria-label={t('statements.externalInvoice.remove')}
                            title={t('statements.externalInvoice.remove')}
                          >
                            <Undo2 className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => downloadProof(line)}
                          aria-label={t('statements.externalInvoice.downloadProof')}
                          title={t('statements.externalInvoice.downloadProof')}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Bloc 2 — le versement. C'est ici, et seulement ici, qu'apparaît la
            commission : la soustraire des lignes de vente rendrait la
            déclaration de TVA de l'organisateur impossible. */}
        <Card>
          <CardContent className="p-0">
            <div className="border-b px-4 py-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide">
                {t('statements.payoutBlock')}
              </h2>
            </div>
            <dl className="divide-y text-sm">
              <div className="flex justify-between px-4 py-2">
                <dt className="text-muted-foreground">{t('statements.periodSales')}</dt>
                <dd className="tabular-nums">{euros(detail.totals.gross_cents)}</dd>
              </div>
              {detail.totals.invoiced_elsewhere_cents > 0 && (
                <div className="flex justify-between px-4 py-2">
                  <dt className="text-muted-foreground">{t('statements.externalInvoice.payoutLine')}</dt>
                  <dd className="tabular-nums">{euros(detail.totals.invoiced_elsewhere_cents)}</dd>
                </div>
              )}
              <div className="flex justify-between px-4 py-2">
                <dt className="text-muted-foreground">{t('statements.commission')}</dt>
                <dd className="tabular-nums">− {euros(detail.totals.commission_cents)}</dd>
              </div>
              {detail.totals.refunds_cents > 0 && (
                <div className="flex justify-between px-4 py-2">
                  <dt className="text-muted-foreground">{t('statements.refunds')}</dt>
                  <dd className="tabular-nums">− {euros(detail.totals.refunds_cents)}</dd>
                </div>
              )}
              <div className="flex justify-between px-4 py-3 font-semibold">
                <dt>{t('statements.transferred')}</dt>
                <dd className="tabular-nums">{euros(detail.totals.transferred_cents)}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground">{t('statements.compensationNotice')}</p>

        <Dialog open={!!invoiceTarget} onOpenChange={(open) => !open && setInvoiceTarget(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('statements.externalInvoice.dialogTitle')}</DialogTitle>
              {invoiceTarget && (
                <DialogDescription>
                  {t('statements.externalInvoice.dialogSale', {
                    team: invoiceTarget.team_name || invoiceTarget.buyer_name || '—',
                    amount: euros(invoiceTarget.amount_gross_cents),
                    date: new Date(invoiceTarget.sold_at).toLocaleDateString(i18n.language),
                  })}
                </DialogDescription>
              )}
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="ext-invoice-number">{t('statements.externalInvoice.number')}</Label>
                <Input
                  id="ext-invoice-number"
                  value={invoiceNumber}
                  maxLength={60}
                  onChange={(e) => { setInvoiceNumber(e.target.value); setInvoiceError(null); }}
                  placeholder="2026-018"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ext-invoice-date">{t('statements.externalInvoice.date')}</Label>
                <DateTimeInput
                  id="ext-invoice-date"
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => { setInvoiceDate(e.target.value); setInvoiceError(null); }}
                />
              </div>
              <p className="text-xs text-muted-foreground">{t('statements.externalInvoice.explanation')}</p>
              {invoiceError && <p className="text-xs text-destructive">{invoiceError}</p>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setInvoiceTarget(null)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={saveInvoice} disabled={savingInvoice}>
                {savingInvoice && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Ventes annulées. Chaque ligne porte le numéro du relevé d'origine :
            sans lui, les deux documents ne se rapprochent pas. */}
        {creditNote && (
          <div className="rounded-lg border bg-card">
            <div className="flex flex-col gap-2 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide">
                  {t('statements.creditNoteBlock')}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {t('statements.issuedOn', {
                    number: creditNote.credit_note_number,
                    date: new Date(creditNote.issued_at).toLocaleDateString(i18n.language),
                  })}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 self-start sm:self-auto"
                onClick={() => download(detail.year, detail.month, true)}
              >
                <Download className="mr-2 h-4 w-4" />
                {t('statements.downloadCreditNote')}
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2 font-medium">{t('statements.columns.originStatement')}</th>
                    <th className="px-4 py-2 font-medium">{t('statements.columns.sale')}</th>
                    <th className="px-4 py-2 font-medium">{t('statements.columns.event')}</th>
                    <th className="px-4 py-2 font-medium">{t('statements.columns.team')}</th>
                    <th className="px-4 py-2 text-right font-medium">
                      {t('statements.columns.credited')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {creditNote.lines.map((line, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="whitespace-nowrap px-4 py-2 tabular-nums">
                        {line.origin_statement_number || '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-muted-foreground tabular-nums">
                        {line.origin_sold_at
                          ? new Date(line.origin_sold_at).toLocaleDateString(i18n.language, {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit',
                            })
                          : '—'}
                      </td>
                      <td className="px-4 py-2">{line.event_name || '—'}</td>
                      <td className="px-4 py-2">{line.team_name || line.buyer_name || '—'}</td>
                      <td className="px-4 py-2 text-right font-medium tabular-nums">
                        {euros(line.amount_gross_cents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 font-semibold">
                    <td className="px-4 py-2" colSpan={4}>
                      {t('statements.totalCredited', { count: creditNote.lines.length })}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {euros(creditNote.totals.gross_cents)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="border-t px-4 py-3 text-xs text-muted-foreground">
              {t('statements.creditNoteNotice')}
            </p>
          </div>
        )}
      </div>
    );
  }

  // ── Liste des mois ────────────────────────────────────────────────────────
  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <div className="flex items-center gap-3">
        <FileText className="h-5 w-5 text-muted-foreground" />
        <div>
          <h1 className="text-xl font-semibold md:text-2xl">{t('statements.title')}</h1>
          <p className="text-xs text-muted-foreground md:text-sm">{t('statements.subtitle')}</p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Le cumul encaissé et son découpage par événement (ex-page
          Encaissements), puis les mêmes ventes mois par mois. */}
      <EarningsSummary />

      <h2 className="pt-2 text-base font-semibold">{t('statements.monthlyTitle')}</h2>

      {rows && rows.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            {t('statements.empty')}
          </CardContent>
        </Card>
      )}

      {rows && rows.length > 0 && (
        <div className="rounded-lg border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">{t('statements.columns.period')}</th>
                  <th className="px-4 py-3 text-right font-medium">{t('statements.columns.sales')}</th>
                  <th className="px-4 py-3 text-right font-medium">{t('statements.columns.gross')}</th>
                  <th className="px-4 py-3 font-medium">{t('statements.columns.status')}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={`${row.year}-${row.month}`}
                    className="cursor-pointer border-b last:border-0 hover:bg-muted/40"
                    onClick={() => openDetail(row)}
                  >
                    <td className="px-4 py-3 font-medium capitalize">
                      {monthLabel(row.year, row.month)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{row.sales_count}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {euros(row.total_gross_cents)}
                    </td>
                    <td className="px-4 py-3">
                      {row.status === 'issued' ? (
                        <Badge variant="secondary">{row.statement_number}</Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground">
                          {row.period_closed
                            ? t('statements.pendingIssue')
                            : t('statements.inProgress')}
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {row.downloadable && (
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={t('statements.download')}
                            onClick={(e) => {
                              e.stopPropagation();
                              download(row.year, row.month);
                            }}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Les factures de Pax à l'organisation : commission de service et
          achats de jetons. Émises par Odoo, affichées une fois validées et
          envoyées ; le PDF est celui d'Odoo. */}
      <div className="space-y-3">
        <div>
          <h2 className="text-base font-semibold">{t('statements.invoices.title')}</h2>
          <p className="text-xs text-muted-foreground">{t('statements.invoices.subtitle')}</p>
        </div>
        {invoicesError && (
          <Alert variant="destructive">
            <AlertDescription>{invoicesError}</AlertDescription>
          </Alert>
        )}
        {!invoicesError && invoices === null && (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        )}
        {invoices && invoices.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('statements.invoices.empty')}</p>
        )}
        {invoices && invoices.length > 0 && (
          <div className="rounded-lg border bg-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-3 font-medium">{t('statements.invoices.number')}</th>
                    <th className="px-4 py-3 font-medium">{t('statements.invoices.object')}</th>
                    <th className="px-4 py-3 font-medium">{t('statements.invoices.date')}</th>
                    <th className="px-4 py-3 text-right font-medium">{t('statements.invoices.amount')}</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b last:border-0">
                      <td className="px-4 py-3 font-medium tabular-nums">{invoice.number}</td>
                      <td className="px-4 py-3">
                        {invoice.kind === 'commission' && invoice.period_year && invoice.period_month
                          ? t('statements.invoices.commission', {
                              period: monthLabel(invoice.period_year, invoice.period_month),
                            })
                          : t('statements.invoices.tokens')}
                      </td>
                      <td className="px-4 py-3 tabular-nums">
                        {invoice.date
                          ? new Date(invoice.date).toLocaleDateString(i18n.language)
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {euros(invoice.amount_total_cents)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={t('statements.download')}
                          onClick={() => downloadInvoice(invoice)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Statements;
